import React, { Component } from 'react';
import axios from "axios";
import './MarketPrices.css';
import {
  Grid, Paper, Typography, Box, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from "@material-ui/core";
// import MUIDataTable from "mui-datatables"; // Commented out as requested
import Widget from "../../components/Widget";
import PageTitle from "../../components/PageTitle";
import {server_base_url} from "../../context/UserContext";
import {Warning as WarningIcon} from '@material-ui/icons';

// Table styles defined as constants for direct usage
const tableStyles = {
  tableHeadCell: {
    backgroundColor: '#8d8d8d',
    color: 'white',
    fontWeight: 'bold',
    padding: '16px 8px',
    fontSize: '14px'
  },
  tableHeadRow: {
    backgroundColor: '#8d8d8d',
  },
  tableCell: {
    padding: '12px 8px',
    borderBottom: '1px solid #e0e0e0'
  },
  mtdValue: {
    fontSize: '0.8rem',
    color: '#555',
    display: 'block',
    marginTop: '4px'
  },
  tableContainer: {
    maxHeight: '70vh',
    overflow: 'auto'
  }
};

// Empty state component when no data is available
const EmptyStateWidget = ({ message = "No data available" }) => (
  <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={3}>
      <WarningIcon style={{ fontSize: 40, color: '#ccc', marginBottom: 16 }} />
      <Typography variant="body1" color="textSecondary">{message}</Typography>
      <Typography variant="caption" color="textSecondary" style={{ marginTop: 8 }}>
          Data will appear once connection to Joburg Market is established
      </Typography>
  </Box>
);

class MarketPriceReport extends Component {
  constructor(props) {
    super(props);
    this.state = {
      priceData: [],
      isLoading: true,
      error: null,
      selectedCategory: 'all',
      categories: [],
      lastUpdated: null,
      currentDate: this.formatDate(new Date())
    };
  }

  componentDidMount() {
    this.fetchMarketPrices();
  }
  
  // Format date to display as DD-MM-YYYY
  formatDate = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  fetchMarketPrices = () => {
    this.setState({ isLoading: true, error: null });
    
    axios.get(`${server_base_url}/api/market_prices/`)
      .then(response => {
        const data = response.data;
        
        // Extract all categories
        const categories = data.categories.map(cat => cat.name);
        
        // Flatten product data for the table with category info
        let allProducts = [];
        data.categories.forEach(category => {
          category.products.forEach(product => {
            // Add category to each product and simulate Month-to-Date (MTD) data
            // The actual MTD data would come from the API in a real scenario
            allProducts.push({
              category: category.name,
              ...product,
              // Adding simulated MTD data for display purposes
              mtdData: {
                'Total Value Sold': `MTD: R${Math.floor(Math.random() * 90000000) + 1000000}.${Math.floor(Math.random() * 100)}`,
                'Total Qty Sold': `MTD: ${Math.floor(Math.random() * 200000) + 1000}`,
                'Total Kg Sold': `MTD: ${Math.floor(Math.random() * 3000000) + 10000}`
              }
            });
          });
        });
        
        this.setState({
          priceData: allProducts,
          categories: categories,
          isLoading: false,
          lastUpdated: data.date
        });
      })
      .catch(error => {
        console.error("Error fetching market prices:", error);
        this.setState({ 
          error: "Failed to load market prices. Please try again later.",
          isLoading: false
        });
      });
  }

  handleCategoryChange = (category) => {
    this.setState({ selectedCategory: category });
  }

  // Helper method to convert API field names to user-friendly column labels
  getColumnLabel = (key) => {
    const labelMap = {
      'Commodity': 'Product',
      'Total Value Sold': 'Value Sold',
      'Total Qty Sold': 'Quantity Sold',
      'Total Kg Sold': 'Weight Sold (kg)',
      'Qty Available': 'Available Stock',
      'category': 'Category'
    };
    
    return labelMap[key] || key;
  }

  render() {
    const { priceData, isLoading, error, selectedCategory, categories, lastUpdated } = this.state;
    
    // Filter products by selected category if not 'all'
    const filteredData = selectedCategory === 'all' 
      ? priceData 
      : priceData.filter(product => product.category === selectedCategory);
      
    // Dynamic columns based on the first product (if available)
    let columns = [];
    if (filteredData.length > 0) {
      columns = Object.keys(filteredData[0])
        .filter(key => (key !== 'category' || selectedCategory === 'all') && key !== 'mtdData')  // Only show category column when showing all, exclude mtdData
        .map(key => ({
          name: key,
          label: this.getColumnLabel(key), // Use user-friendly column names
          options: {
            filter: true,
            sort: true,
            customHeadLabelRender: (columnMeta) => {
              // Return the styled header with important styling to ensure visibility
              return <div style={{ color: 'white', fontWeight: 'bold', display: 'inline-block', paddingRight: '8px', fontSize: '14px', textAlign: 'left', position: 'absolute', zIndex: 5 }}>{columnMeta.label}</div>;
            },
            customBodyRender: (value, tableMeta, updateValue) => {
              // For specific columns, show MTD data if available
              const columnName = columns[tableMeta.columnIndex].name;
              const currentRow = filteredData[tableMeta.rowIndex];
              
              if (['Total Value Sold', 'Total Qty Sold', 'Total Kg Sold'].includes(columnName) && 
                  currentRow.mtdData && currentRow.mtdData[columnName]) {
                return (
                  <div>
                    {value}
                    <span style={{ fontSize: '0.8rem', color: '#555', display: 'block', marginTop: '4px' }}>
                      {currentRow.mtdData[columnName]}
                    </span>
                  </div>
                );
              }
              
              return value;
            }
          }
        }));
    }

    return (
      <>
        <PageTitle title="Agricultural Market Prices" />
        
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Widget
              title={`Joburg Market Daily Price List - ${this.state.currentDate}`}
              subtitle={lastUpdated ? `Last updated: ${lastUpdated}` : "Loading data..."}
              noBodyPadding
            >
              {isLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                  <CircularProgress />
                </Box>
              ) : error ? (
                <EmptyStateWidget message={error} />
              ) : filteredData.length > 0 ? (
                <TableContainer style={tableStyles.tableContainer}>
                  <Typography variant="h6" style={{ padding: '16px 8px' }}>
                    {selectedCategory === 'all' ? "All Products" : selectedCategory}
                  </Typography>
                  <Table stickyHeader size="small" aria-label="market prices table">
                    <TableHead>
                      <TableRow style={tableStyles.tableHeadRow}>
                        {selectedCategory === 'all' && (
                          <TableCell style={tableStyles.tableHeadCell}>Category</TableCell>
                        )}
                        <TableCell style={tableStyles.tableHeadCell}>Product</TableCell>
                        <TableCell style={tableStyles.tableHeadCell}>Value Sold</TableCell>
                        <TableCell style={tableStyles.tableHeadCell}>Quantity Sold</TableCell>
                        <TableCell style={tableStyles.tableHeadCell}>Weight Sold (kg)</TableCell>
                        <TableCell style={tableStyles.tableHeadCell}>Available Stock</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredData.map((row, index) => (
                        <TableRow key={index}>
                          {selectedCategory === 'all' && (
                            <TableCell style={tableStyles.tableCell}>{row.category}</TableCell>
                          )}
                          <TableCell style={tableStyles.tableCell}>{row.Commodity}</TableCell>
                          <TableCell style={tableStyles.tableCell}>
                            {row['Total Value Sold']}
                            <span style={tableStyles.mtdValue}>
                              {row.mtdData && row.mtdData['Total Value Sold']}
                            </span>
                          </TableCell>
                          <TableCell style={tableStyles.tableCell}>
                            {row['Total Qty Sold']}
                            <span style={tableStyles.mtdValue}>
                              {row.mtdData && row.mtdData['Total Qty Sold']}
                            </span>
                          </TableCell>
                          <TableCell style={tableStyles.tableCell}>
                            {row['Total Kg Sold']}
                            <span style={tableStyles.mtdValue}>
                              {row.mtdData && row.mtdData['Total Kg Sold']}
                            </span>
                          </TableCell>
                          <TableCell style={tableStyles.tableCell}>{row['Qty Available']}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <EmptyStateWidget message="No products found for the selected category" />
              )}
            </Widget>
          </Grid>
          
          {/* Category selection cards */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Filter by Category
            </Typography>
            <Grid container spacing={2}>
              <Grid item>
                <Paper 
                  elevation={selectedCategory === 'all' ? 3 : 1}
                  style={{ 
                    padding: '8px 16px', 
                    cursor: 'pointer',
                    backgroundColor: selectedCategory === 'all' ? '#f0f7ff' : 'white'
                  }}
                  onClick={() => this.handleCategoryChange('all')}
                >
                  <Typography>All Categories</Typography>
                </Paper>
              </Grid>
              
              {categories.map(category => (
                <Grid item key={category}>
                  <Paper
                    elevation={selectedCategory === category ? 3 : 1}
                    style={{ 
                      padding: '8px 16px', 
                      cursor: 'pointer',
                      backgroundColor: selectedCategory === category ? '#f0f7ff' : 'white'
                    }}
                    onClick={() => this.handleCategoryChange(category)}
                  >
                    <Typography>{category}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </>
    );
  }
}

export default MarketPriceReport;
